<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { cortexMemoryAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'next/input/Input.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const profile = ref({});
const facts = ref([]);
const newFact = reactive({ key: '', value: '' });
const purgeDialogRef = ref(null);

const interactions = computed(() => profile.value.recent_interactions || []);
const canAddFact = computed(
  () => newFact.key.trim().length > 0 && newFact.value.trim().length > 0
);

const loadMemory = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await cortexMemoryAPI.context({
      contact_id: props.contactId,
      ...(props.conversationId
        ? { conversation_id: props.conversationId }
        : {}),
    });
    profile.value = data.contact_profile || {};
    facts.value = (profile.value.facts || []).map(fact => ({ ...fact }));
  } catch {
    profile.value = {};
    facts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const saveProfile = async () => {
  isSaving.value = true;
  try {
    await cortexMemoryAPI.updateContact({
      contact_id: props.contactId,
      payload: {
        ...profile.value,
        schema: 'cortex_contact_profile.v1',
        facts: facts.value,
      },
    });
    useAlert(t('CORTEX.MEMORY.SAVE_SUCCESS'));
  } catch {
    useAlert(t('CORTEX.MEMORY.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const addFact = async () => {
  if (!canAddFact.value) return;
  facts.value.push({
    key: newFact.key.trim(),
    value: newFact.value.trim(),
    source: 'manual',
  });
  newFact.key = '';
  newFact.value = '';
  await saveProfile();
};

const updateFact = (index, field, value) => {
  facts.value[index] = {
    ...facts.value[index],
    [field]: value,
    source: 'manual',
  };
};

const removeFact = async index => {
  facts.value.splice(index, 1);
  await saveProfile();
};

const purgeMemory = async () => {
  try {
    await cortexMemoryAPI.purgeContact(props.contactId);
    profile.value = {};
    facts.value = [];
    purgeDialogRef.value?.close();
    useAlert(t('CORTEX.MEMORY.PURGE_SUCCESS'));
  } catch {
    useAlert(t('CORTEX.MEMORY.PURGE_ERROR'));
  }
};

const requestPurgeMemory = () => purgeDialogRef.value?.open();

watch(() => [props.contactId, props.conversationId], loadMemory, {
  immediate: true,
});
</script>

<template>
  <div class="space-y-4 py-1">
    <p v-if="isLoading" class="text-sm text-n-slate-10">
      {{ $t('CORTEX.MEMORY.LOADING') }}
    </p>

    <template v-else>
      <div v-if="facts.length" class="space-y-2">
        <div
          v-for="(fact, index) in facts"
          :key="`${fact.key}-${index}`"
          class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <div class="mb-2 flex items-center justify-between gap-2">
            <span
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
            >
              {{
                fact.source === 'manual'
                  ? $t('CORTEX.MEMORY.SOURCE_MANUAL')
                  : $t('CORTEX.MEMORY.SOURCE_AI')
              }}
            </span>
            <NextButton
              icon="i-lucide-trash-2"
              color="ruby"
              variant="ghost"
              size="sm"
              :label="$t('CORTEX.MEMORY.REMOVE_FACT')"
              @click="removeFact(index)"
            />
          </div>
          <div class="grid gap-2">
            <NextInput
              :model-value="fact.key"
              :label="$t('CORTEX.MEMORY.FACT_KEY')"
              @update:model-value="updateFact(index, 'key', $event)"
            />
            <NextInput
              :model-value="fact.value"
              :label="$t('CORTEX.MEMORY.FACT_VALUE')"
              @update:model-value="updateFact(index, 'value', $event)"
            />
            <NextButton
              :label="$t('CORTEX.MEMORY.SAVE_FACT')"
              size="sm"
              :is-loading="isSaving"
              @click="saveProfile"
            />
          </div>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-10">
        {{ $t('CORTEX.MEMORY.EMPTY') }}
      </p>

      <div class="space-y-2 border-t border-n-weak pt-4">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('CORTEX.MEMORY.ADD_FACT') }}
        </h4>
        <NextInput
          v-model="newFact.key"
          :label="$t('CORTEX.MEMORY.FACT_KEY')"
        />
        <NextInput
          v-model="newFact.value"
          :label="$t('CORTEX.MEMORY.FACT_VALUE')"
        />
        <NextButton
          :label="$t('CORTEX.MEMORY.ADD_FACT')"
          size="sm"
          :disabled="!canAddFact"
          :is-loading="isSaving"
          @click="addFact"
        />
      </div>

      <div
        v-if="interactions.length"
        class="space-y-2 border-t border-n-weak pt-4"
      >
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('CORTEX.MEMORY.RECENT_INTERACTIONS') }}
        </h4>
        <p
          v-for="interaction in interactions"
          :key="interaction.conversation_id"
          class="text-sm text-n-slate-11"
        >
          {{ interaction.summary }}
        </p>
      </div>

      <div class="border-t border-n-weak pt-4">
        <NextButton
          :label="$t('CORTEX.MEMORY.PURGE')"
          color="ruby"
          variant="outline"
          size="sm"
          @click="requestPurgeMemory"
        />
      </div>
    </template>

    <Dialog
      ref="purgeDialogRef"
      type="alert"
      width="sm"
      :title="$t('CORTEX.MEMORY.PURGE')"
      :description="$t('CORTEX.MEMORY.PURGE_CONFIRM')"
      :confirm-button-label="$t('CORTEX.MEMORY.PURGE')"
      @confirm="purgeMemory"
    />
  </div>
</template>
